import type { Curve, HeatmapCurve, ParsedData } from '../../../../interfaces/toolData';
import type { WaveFileType } from '../../types';
import { cleanTimeSeriesData } from '../../../dataQuality';

/**
 * Parser for JAE Smart DM ROTGAMMA files
 * 
 * Handles azimuthal gamma ray measurements across 16 sectors for heatmap visualization:
 * - AZG_SECT_1 through AZG_SECT_16 (azimuthal gamma sectors)
 * - RPM data (rotation measurements)
 * - Survey toolface offset
 * - Cold/hot sector indicators
 */
export class SmartDmRotGammaParser {
  static parse(fileContent: string): ParsedData {
    const lines = fileContent.split('\n');
    
    // Validate file format
    if (!lines[0]?.trim().startsWith('SmartDM')) {
      throw new Error('Invalid JAE Smart DM file format');
    }
    
    if (!lines[1]?.trim().includes('ROTGAMMA Log')) {
      throw new Error('Not a JAE Smart DM ROTGAMMA file');
    }
    
    // Find CSV header and data start
    const { metadata, dataHeaderIndex, dataStartIndex } = this.findDataStart(lines);
    
    if (dataHeaderIndex === -1) {
      throw new Error('Could not find data header in JAE Smart DM ROTGAMMA file');
    }
    
    // Parse CSV data
    const headerLine = lines[dataHeaderIndex];
    const columnNames = headerLine.split(',').map(name => name.trim());
    
    const dataLines = lines.slice(dataStartIndex).filter(line => line.trim());
    const rawData: Record<string, string>[] = [];
    
    for (const line of dataLines) {
      const values = line.split(',');
      const record: Record<string, string> = {};
      
      columnNames.forEach((colName, index) => {
        if (index < values.length) {
          record[colName] = values[index]?.trim() || '';
        }
      });
      
      rawData.push(record);
    }
    
    if (rawData.length === 0) {
      throw new Error('No valid data found in JAE Smart DM ROTGAMMA file');
    }
    
    // Generate curves and heatmap data
    const { curves, heatmapCurves } = this.parseRotGammaData(rawData);
    
    return {
      toolType: 'JAE',
      fileType: 'JAE_SMARTDM_ROTGAMMA' as WaveFileType,
      curves,
      heatmapCurves,
      metadata,
      dataPreview: {
        totalCount: rawData.length,
        first5: rawData.slice(0, 5)
      }
    };
  }
  
  private static findDataStart(lines: string[]): {
    metadata: Record<string, string>;
    dataHeaderIndex: number;
    dataStartIndex: number;
  } {
    const metadata: Record<string, string> = {};
    let dataHeaderIndex = -1;
    
    // Search for the CSV header line by looking for RTC_TIME and azimuthal sectors
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this line contains the ROTGAMMA header signature
      if (line.includes('RTC_TIME') && line.includes('AZG_SECT_1') && line.includes(',')) {
        dataHeaderIndex = i;
        break;
      }
      
      // Extract metadata from lines before the CSV header
      if (line.includes(':') && !line.includes(',')) {
        const [key, ...valueParts] = line.split(':');
        const value = valueParts.join(':').trim();
        
        if (key && value) {
          metadata[key.trim()] = value;
        }
      }
    }
    
    return {
      metadata,
      dataHeaderIndex,
      dataStartIndex: dataHeaderIndex + 1
    };
  }
  
  private static parseRotGammaData(data: Record<string, string>[]): {
    curves: Curve[];
    heatmapCurves: HeatmapCurve[];
  } {
    const curves: Curve[] = [];
    const heatmapCurves: HeatmapCurve[] = [];
    
    // Find azimuthal sector columns (AZG_SECT_1 through AZG_SECT_16)
    const sectorColumns = this.findSectorColumns(data[0]);
    
    if (sectorColumns.length === 0) {
      throw new Error('No azimuthal gamma sectors found in ROTGAMMA data');
    }
    
    // Parse timestamps and azimuthal data
    const timestamps: Date[] = [];
    const azimuthalValues: number[][] = [];
    
    // Data for regular curves
    const rpmData: { timestamps: Date[], values: number[] } = { timestamps: [], values: [] };
    const rpmMinData: { timestamps: Date[], values: number[] } = { timestamps: [], values: [] };
    const rpmMaxData: { timestamps: Date[], values: number[] } = { timestamps: [], values: [] };
    const surveyOffsetData: { timestamps: Date[], values: number[] } = { timestamps: [], values: [] };
    const coldSectorData: { timestamps: Date[], values: number[] } = { timestamps: [], values: [] };
    const hotSectorData: { timestamps: Date[], values: number[] } = { timestamps: [], values: [] };
    
    for (const row of data) {
      const timestampStr = row['RTC_TIME'];
      if (!timestampStr) continue;
      
      const timestamp = new Date(timestampStr);
      if (isNaN(timestamp.getTime())) continue;
      
      timestamps.push(timestamp);
      
      // Parse azimuthal sector values
      const sectorValues: number[] = [];
      for (const sectorCol of sectorColumns) {
        const value = parseFloat(row[sectorCol] || '0');
        sectorValues.push(isNaN(value) ? 0 : value);
      }
      azimuthalValues.push(sectorValues);
      
      // Parse RPM data
      const rpmValue = parseFloat(row['RPM'] || '');
      if (!isNaN(rpmValue)) {
        rpmData.timestamps.push(timestamp);
        rpmData.values.push(rpmValue);
      }
      
      // Parse RPM_MIN data
      const rpmMinValue = parseFloat(row['RPM_MIN'] || '');
      if (!isNaN(rpmMinValue)) {
        rpmMinData.timestamps.push(timestamp);
        rpmMinData.values.push(rpmMinValue);
      }
      
      // Parse RPM_MAX data
      const rpmMaxValue = parseFloat(row['RPM_MAX'] || '');
      if (!isNaN(rpmMaxValue)) {
        rpmMaxData.timestamps.push(timestamp);
        rpmMaxData.values.push(rpmMaxValue);
      }
      
      // Parse survey toolface offset
      const surveyOffset = parseFloat(row['AZG_SURVEY_TF_OFFSET'] || '');
      if (!isNaN(surveyOffset)) {
        surveyOffsetData.timestamps.push(timestamp);
        surveyOffsetData.values.push(surveyOffset);
      }
      
      // Parse cold sector indicator
      const coldSector = parseFloat(row['AZG_COLD_SECT'] || '');
      if (!isNaN(coldSector)) {
        coldSectorData.timestamps.push(timestamp);
        coldSectorData.values.push(coldSector);
      }
      
      // Parse hot sector indicator
      const hotSector = parseFloat(row['AZG_HOT_SECT'] || '');
      if (!isNaN(hotSector)) {
        hotSectorData.timestamps.push(timestamp);
        hotSectorData.values.push(hotSector);
      }
    }
    
    // Create heatmap curve for azimuthal gamma
    if (timestamps.length > 0 && azimuthalValues.length > 0) {
      heatmapCurves.push({
        name: 'Azimuthal Gamma',
        xValues: timestamps,
        yValues: azimuthalValues,
        yUnit: 'API'
      });
    }
    
    // Create regular curves
    if (rpmData.timestamps.length > 0) {
      const cleanupResult = cleanTimeSeriesData({
        name: 'RPM',
        xValues: rpmData.timestamps,
        yValues: rpmData.values,
        yUnit: 'RPM'
      });
      curves.push(cleanupResult.cleanedCurve);
    }
    
    if (rpmMinData.timestamps.length > 0) {
      const cleanupResult = cleanTimeSeriesData({
        name: 'RPM Min',
        xValues: rpmMinData.timestamps,
        yValues: rpmMinData.values,
        yUnit: 'RPM'
      });
      curves.push(cleanupResult.cleanedCurve);
    }
    
    if (rpmMaxData.timestamps.length > 0) {
      const cleanupResult = cleanTimeSeriesData({
        name: 'RPM Max',
        xValues: rpmMaxData.timestamps,
        yValues: rpmMaxData.values,
        yUnit: 'RPM'
      });
      curves.push(cleanupResult.cleanedCurve);
    }
    
    if (surveyOffsetData.timestamps.length > 0) {
      const cleanupResult = cleanTimeSeriesData({
        name: 'Survey Toolface Offset',
        xValues: surveyOffsetData.timestamps,
        yValues: surveyOffsetData.values,
        yUnit: '°'
      });
      curves.push(cleanupResult.cleanedCurve);
    }
    
    if (coldSectorData.timestamps.length > 0) {
      const cleanupResult = cleanTimeSeriesData({
        name: 'Cold Sector',
        xValues: coldSectorData.timestamps,
        yValues: coldSectorData.values,
        yUnit: 'sector'
      });
      curves.push(cleanupResult.cleanedCurve);
    }
    
    if (hotSectorData.timestamps.length > 0) {
      const cleanupResult = cleanTimeSeriesData({
        name: 'Hot Sector',
        xValues: hotSectorData.timestamps,
        yValues: hotSectorData.values,
        yUnit: 'sector'
      });
      curves.push(cleanupResult.cleanedCurve);
    }
    
    return { curves, heatmapCurves };
  }
  
  private static findSectorColumns(sampleRow: Record<string, string>): string[] {
    const sectorColumns: string[] = [];
    
    // Look for AZG_SECT_1 through AZG_SECT_16
    for (let i = 1; i <= 16; i++) {
      const colName = `AZG_SECT_${i}`;
      if (colName in sampleRow) {
        sectorColumns.push(colName);
      }
    }
    
    return sectorColumns;
  }
} 